const TOKEN_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function isValidUnsubscribeToken(value: string | null | undefined): value is string {
  return typeof value === 'string' && TOKEN_RE.test(value)
}

/**
 * The one supported outbound-link builder. A fragment is never included in an
 * HTTP request, Referer header, CDN cache key, or platform access log.
 */
export function buildUnsubscribeUrl(
  token: string,
  origin = 'https://microplasticsapp.com'
): string {
  if (!isValidUnsubscribeToken(token)) {
    throw new Error('A valid unsubscribe token is required')
  }
  const url = new URL('/unsubscribe', origin)
  url.hash = new URLSearchParams({ token }).toString()
  return url.toString()
}

export function readUnsubscribeTokenFromHash(hash: string): string | null {
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash)
  const token = params.get('token')
  return isValidUnsubscribeToken(token) ? token : null
}

interface LegacyUrlTarget {
  location: { href: string }
  history: {
    state?: unknown
    replaceState(data: unknown, unused: string, url?: string | URL | null): void
  }
}

/**
 * Backward compatibility for already-issued `?token=` links. Run during the
 * earliest client bootstrap, before any optional analytics initialization.
 * The initial request may already exist in platform logs, so rollout still
 * needs the external legacy-log gate documented in the release runbook.
 */
export function promoteLegacyUnsubscribeQueryToFragment(
  target: LegacyUrlTarget
): boolean {
  let url: URL
  try {
    url = new URL(target.location.href)
  } catch {
    return false
  }
  if (!/^\/unsubscribe(?:\/|$)/.test(url.pathname) || url.search.length === 0) {
    return false
  }

  const fragmentToken = readUnsubscribeTokenFromHash(url.hash)
  const queryToken = url.searchParams.get('token')
  if (!fragmentToken && isValidUnsubscribeToken(queryToken)) {
    url.hash = new URLSearchParams({ token: queryToken }).toString()
  }

  // Strip the entire query, not just `token`, so unrelated query data cannot
  // survive into history, referrers, analytics, or a copied link.
  url.search = ''
  target.history.replaceState(
    target.history.state ?? null,
    '',
    `${url.pathname}${url.hash}`
  )
  return true
}
